// A room made in the browser from nothing, saved as a brand-new seed — v1,
// no parent. There is no seed to commit to (a jam needs one), so it goes up
// the way any new seed does, through /api/uploads (shared/upload/
// publish-seed.ts): the arrangement, the mixdown as its master, and the
// bytes of whatever audio the pool doesn't already hold (shared/upload/
// room-seed.ts decides which).
//
// Nothing reports success it has not had confirmed: a failed step aborts the
// pending seed, and the caller is handed the seed id only after /complete.

import { poolHashAnyChannel } from "@shared/blobs.js";
import type { CanonicalRoom } from "@shared/sync/state.js";
import {
  abortSeedUpload,
  completeSeedUpload,
  openSeedUpload,
  prepareSeed,
  putUploadJob,
  type CompletedFile,
  type PreviewEncoder,
  type SeedbedTransport,
  type SeedClipBytes,
  type SeedUploadSession,
} from "@shared/upload/publish-seed.js";
import { planRoomSeed, roomHasContent } from "@shared/upload/room-seed.js";
import { encodePreviews } from "@upload-core/preview-encode.js";
import { webTransport } from "@web-shared/lib/audiotool-upload";
import { encodeWav24 } from "@web-shared/lib/wav";
import { limitProblem } from "@/lib/web-upload/limits";

export type SaveStage = "rendering" | "preparing" | "uploading" | "finishing";

export interface SaveRootSeedInput {
  room: CanonicalRoom;
  tempo: number;
  title: string;
  /** "" when not given — never guessed. */
  key: string;
  note: string | null;
  /** Bytes for a key the pool doesn't hold: a take held in the tab, or a
   * fetch. Null when they can't be had — the save then stops. */
  bytesOf: (key: string) => Promise<Uint8Array | null>;
  /** The room's mixdown — what the seed page plays. */
  renderMaster: () => Promise<AudioBuffer>;
  onStage?: (stage: SaveStage) => void;
}

/** Thrown with a sentence the producer can read. */
export class SaveProblem extends Error {}

const CONCURRENCY = 3;

/** The I/O, swappable for a spec. */
export interface SaveDeps {
  transport: SeedbedTransport;
  encodePreviews: PreviewEncoder;
}

const LIVE: SaveDeps = { transport: webTransport, encodePreviews };

export async function saveRoomAsNewSeed(input: SaveRootSeedInput, deps: SaveDeps = LIVE): Promise<{ seedId: string }> {
  const { transport } = deps;
  const { room, tempo } = input;
  if (!roomHasContent(room)) {
    throw new SaveProblem("There's nothing to save yet — add a note or a sound first.");
  }

  input.onStage?.("rendering");
  const buffer = await input.renderMaster();
  const master = buffer.length > 0 && buffer.sampleRate > 0 ? new Uint8Array(encodeWav24(buffer)) : null;
  if (!master) throw new SaveProblem("The mixdown came out empty, so there's nothing to play. Nothing was saved.");

  input.onStage?.("preparing");
  const plan = planRoomSeed(room, tempo, (k) => poolHashAnyChannel(k) !== null);
  const clips: SeedClipBytes[] = [];
  const sessionClips: SeedClipBytes[] = [];
  const fetched = new Map<string, Uint8Array>();
  for (const need of plan.needsBytes) {
    let bytes = fetched.get(need.sourceKey) ?? null;
    if (!bytes) {
      bytes = await input.bytesOf(need.sourceKey);
      if (!bytes) throw new SaveProblem("A clip's audio couldn't be read back, so it can't be saved. Nothing was saved.");
      fetched.set(need.sourceKey, bytes);
    }
    (need.list === "session" ? sessionClips : clips).push({ trackIndex: need.trackIndex, clipIndex: need.clipIndex, bytes });
  }
  const sizes = [...fetched.values(), master].map((b, i) => ({ name: i === fetched.size ? "the mix" : `clip ${i + 1}`, uploadBytes: b.byteLength }));
  const tooBig = limitProblem(sizes);
  if (tooBig) throw new SaveProblem(tooBig);

  const prepared = await prepareSeed(
    {
      meta: {
        title: input.title,
        bpm: tempo,
        key: input.key,
        parentSeedId: null,
        note: input.note,
        origin: "web_upload",
      },
      snapshot: plan.snapshot,
      clips,
      sessionClips,
      pooled: plan.pooled,
      master,
    },
    deps.encodePreviews,
  );

  input.onStage?.("uploading");
  let session: SeedUploadSession | null = null;
  try {
    session = await openSeedUpload(transport, prepared);
    const open = session;
    const completed = new Map<string, CompletedFile>();
    const queue = [...open.jobs];
    const worker = async (): Promise<void> => {
      for (let job = queue.shift(); job; job = queue.shift()) {
        completed.set(job.id, await putUploadJob(transport, job));
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));

    input.onStage?.("finishing");
    await completeSeedUpload(transport, open, open.jobs.map((j) => completed.get(j.id)!));
    return { seedId: open.seedId };
  } catch (err) {
    if (session) await abortSeedUpload(transport, session);
    throw err;
  }
}
